import { Head, router } from "@inertiajs/react"
import { useState, useCallback, type ReactNode } from "react"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { AdminLayout } from "@/layouts/AdminLayout"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type {
  AnalyticsDateRange,
  TwoLayerLift,
  TwoLayerLiftSeriesPoint,
} from "@/lib/types"

interface Props {
  current: TwoLayerLift
  series: TwoLayerLiftSeriesPoint[]
  date_range: AnalyticsDateRange
  insufficient_data: boolean
}

const PRESET_DAYS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
] as const

const TWO_LAYER_COLOR  = "#C2553A" // brand terracotta (per DESIGN.md)
const HOTEL_ONLY_COLOR = "#6b7280" // neutral grey for the control arm

function formatLift(lift: number | null): string {
  if (lift === null) return "—"
  const sign = lift > 0 ? "+" : ""
  return `${sign}${lift.toFixed(1)}%`
}

function liftToneClass(lift: number | null): string {
  if (lift === null) return "text-muted-foreground"
  if (lift > 0) return "text-emerald-600 dark:text-emerald-400"
  if (lift < 0) return "text-destructive"
  return "text-muted-foreground"
}

export default function TwoLayerLiftValidation({
  current,
  series,
  date_range,
  insufficient_data,
}: Props) {
  const [customFrom, setCustomFrom] = useState(date_range.from)
  const [customTo, setCustomTo] = useState(date_range.to)
  const [showCustom, setShowCustom] = useState(false)
  const [navigating, setNavigating] = useState(false)
  const [customError, setCustomError] = useState<string | null>(null)

  const activeDays = (() => {
    const from = new Date(date_range.from)
    const to = new Date(date_range.to)
    const diff = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
    return PRESET_DAYS.find(({ days }) => days === diff)?.days ?? null
  })()

  const navigate = useCallback(
    (params: Record<string, string | undefined>) => {
      setNavigating(true)
      router.get(
        window.location.pathname,
        params,
        {
          preserveState: false,
          onFinish: () => setNavigating(false),
        },
      )
    },
    [],
  )

  function applyPreset(days: number) {
    setShowCustom(false)
    setCustomError(null)
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - days)
    const fromStr = from.toISOString().slice(0, 10)
    const toStr = to.toISOString().slice(0, 10)
    setCustomFrom(fromStr)
    setCustomTo(toStr)
    navigate({ from: fromStr, to: toStr })
  }

  function applyCustom() {
    setCustomError(null)
    if (!customFrom || !customTo) {
      setCustomError("Please enter both a start and end date.")
      return
    }
    if (customFrom > customTo) {
      setCustomError("Start date must be on or before the end date.")
      return
    }
    navigate({ from: customFrom, to: customTo })
  }

  return (
    <>
      <Head title="Two-Layer Lift Validation" />

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Two-Layer Lift Validation</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Do guests on a two-layer guide engage with local POIs at a higher rate than guests on a
            hotel-only guide? This page answers that question with cohort-split engagement data.
          </p>
        </div>

        {/* Date range controls — match the analytics index pattern */}
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Date range presets">
            {PRESET_DAYS.map(({ label, days }) => (
              <Button
                key={label}
                variant={activeDays === days ? "secondary" : "outline"}
                size="sm"
                onClick={() => applyPreset(days)}
                disabled={navigating}
                aria-pressed={activeDays === days}
              >
                {label}
              </Button>
            ))}
          </div>

          <Button
            variant={showCustom ? "secondary" : "outline"}
            size="sm"
            onClick={() => {
              setShowCustom((v) => !v)
              setCustomError(null)
            }}
            aria-expanded={showCustom}
          >
            Custom
          </Button>

          {showCustom && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="lift-from" className="text-xs text-muted-foreground">From</Label>
                <Input
                  id="lift-from"
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomFrom(e.target.value)
                    setCustomError(null)
                  }}
                  className="h-8 w-36 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="lift-to" className="text-xs text-muted-foreground">To</Label>
                <Input
                  id="lift-to"
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomTo(e.target.value)
                    setCustomError(null)
                  }}
                  className="h-8 w-36 text-sm"
                />
              </div>
              <Button
                size="sm"
                onClick={applyCustom}
                disabled={navigating || !customFrom || !customTo}
              >
                Apply
              </Button>
              {customError && (
                <p role="alert" className="w-full text-xs text-destructive">
                  {customError}
                </p>
              )}
            </div>
          )}

          {navigating && (
            <span className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
              Loading…
            </span>
          )}
        </div>

        {insufficient_data ? (
          <Alert>
            <AlertTitle>Not enough properties to compare cohorts yet</AlertTitle>
            <AlertDescription>
              We need at least {current.min_properties_required} properties in each cohort to show a
              meaningful comparison. Current counts: {" "}
              <strong>{current.two_layer_properties} two-layer</strong> ·{" "}
              <strong>{current.hotel_only_properties} hotel-only</strong>. Add more guides to the
              hotel-only arm to unlock this view.
            </AlertDescription>
          </Alert>
        ) : (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Engagement lift (two-layer vs hotel-only)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <div>
                  <p className={`text-4xl font-semibold tabular-nums ${liftToneClass(current.lift_pct)}`}>
                    {formatLift(current.lift_pct)}
                  </p>
                  <p className="text-xs text-muted-foreground">POI-view rate lift</p>
                </div>
                <div>
                  <p
                    className="text-2xl font-semibold tabular-nums"
                    style={{ color: TWO_LAYER_COLOR }}
                  >
                    {current.two_layer.rate.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Two-layer · {current.two_layer.sessions} sessions · {current.two_layer.poi_views}{" "}
                    POI views
                  </p>
                </div>
                <div>
                  <p
                    className="text-2xl font-semibold tabular-nums"
                    style={{ color: HOTEL_ONLY_COLOR }}
                  >
                    {current.hotel_only.rate.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Hotel-only · {current.hotel_only.sessions} sessions · {current.hotel_only.poi_views}{" "}
                    POI views
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              POI views per session over time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {series.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                No data for this period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={256}>
                <LineChart data={series} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="two_layer_rate"
                    stroke={TWO_LAYER_COLOR}
                    strokeWidth={2}
                    dot={false}
                    name="Two-layer"
                  />
                  <Line
                    type="monotone"
                    dataKey="hotel_only_rate"
                    stroke={HOTEL_ONLY_COLOR}
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={false}
                    name="Hotel-only"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

TwoLayerLiftValidation.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
